import { useCallback, useMemo, useState, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import { ThemeContext } from "./ThemeContext";
import type { ThemeContextValue } from "./ThemeContext";
import {
  DARK_MEDIA_QUERY,
  applyThemePreference,
  readPersistedThemePreference,
  resolveTheme,
  themeCookieSpec,
  writePersistedThemePreference,
} from "./theme";
import type { CookieJar, ThemePreference } from "./theme";
import { getConfig } from "../config/env";

/**
 * The cookie this build reads and writes (`WEB-038`).
 *
 * Resolved from the build-time environment rather than sniffed from `location`:
 * `docs/ENVIRONMENTS.md` makes the environment a compile-time fact, and
 * `scripts/check-deploy-config.mjs` already verifies the built bundle against it. Sniffing
 * would let a staging bundle served from a production host quietly write the production
 * cookie.
 */
const COOKIE = themeCookieSpec(getConfig().environment);

function darkMediaQuery(): MediaQueryList | undefined {
  if (typeof window === "undefined" || !window.matchMedia) return undefined;
  return window.matchMedia(DARK_MEDIA_QUERY);
}

/**
 * `prefers-color-scheme` is an external store, so it is read through
 * useSyncExternalStore rather than mirrored into state: React stays subscribed to the OS
 * and there is no render-then-correct flicker when the visitor is on `system`.
 */
function subscribeToSystemTheme(onChange: () => void): () => void {
  const query = darkMediaQuery();
  if (!query) return () => undefined;
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
}

function getSystemPrefersDark(): boolean {
  return darkMediaQuery()?.matches ?? false;
}

function getStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * The document, as somewhere cookies live. Read through a narrow interface so the rules can
 * be unit-tested without one, exactly as storage is.
 */
function getCookieJar(): CookieJar | undefined {
  return typeof document === "undefined" ? undefined : document;
}

function readCookieString(): string {
  try {
    return getCookieJar()?.cookie ?? "";
  } catch {
    // A browser configured to block cookies throws on the accessor in some builds.
    return "";
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    const stored = readPersistedThemePreference(readCookieString(), getStorage(), COOKIE);
    /*
     * Keep the document in step with the stored value on the very first render, the same way
     * the inline bootstrap in `app/layout.tsx` does before React loads.
     *
     * Guarded since `WEB-044`: this initializer also runs while the page is being prerendered,
     * where there is no `document` at all. `readCookieString` was already written for that
     * case and returns `""`, so the preference resolves to `system` at build time — which is
     * the correct thing for HTML that will be served to everyone — and the browser applies the
     * real preference on hydration, after the inline bootstrap has already prevented the flash.
     */
    if (typeof document !== "undefined") {
      applyThemePreference(document.documentElement, stored);
    }
    return stored;
  });

  const systemPrefersDark = useSyncExternalStore(
    subscribeToSystemTheme,
    getSystemPrefersDark,
    () => false,
  );

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    // Only ever reached from a click, so `document` exists; guarded for symmetry with the
    // initializer above rather than because a build can get here.
    if (typeof document !== "undefined") {
      applyThemePreference(document.documentElement, next);
    }
    writePersistedThemePreference(getCookieJar(), getStorage(), COOKIE, next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolved: resolveTheme(preference, systemPrefersDark),
      setPreference,
    }),
    [preference, systemPrefersDark, setPreference],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}
